'use client'

import clsx from 'clsx'
import { Children, ReactNode, useLayoutEffect, useRef } from 'react'
import { TransitionMapOptions, TransitionStatus, useTransitionMap } from 'react-transition-state'
import { twMerge } from 'tailwind-merge'

const DEFAULT_TRANSITION_MAP_OPTIONS: TransitionMapOptions<number> = {
    timeout: { enter: 300, exit: 150 },
    preEnter: true,
    preExit: true,
    allowMultiple: false,
    mountOnEnter: true,
    unmountOnExit: true,
}

const scrollToPageTop = (element: HTMLElement | null) => {
    if (element?.parentElement) element.parentElement.scrollTop = 0
}

/**
 * State cycle (with mountOnEnter + unmountOnExit):
 * unmounted → preEnter → entering → entered → preExit → exiting → exited → unmounted
 * @see https://github.com/szhsin/react-transition-state?tab=readme-ov-file#state-diagram
 */
const defaultGetStatusClassName = (status: TransitionStatus, isForward: boolean): string => {
    switch (status) {
        case 'preEnter':
            return clsx(
                isForward ? 'translate-x-[5%]' : '-translate-x-[5%]',
                'opacity-0 overflow-hidden',
            )
        case 'entering':
            return 'translate-x-0 opacity-50 overflow-hidden transition-all duration-150 ease-out delay-150'
        case 'entered':
            return 'translate-x-0 opacity-100 transition-opacity duration-100 ease-out'
        case 'preExit':
            return 'translate-x-0 opacity-30 overflow-hidden transition-opacity duration-150 ease-out'
        case 'exiting':
        case 'exited':
            return clsx(
                isForward ? '-translate-x-[5%]' : 'translate-x-[10%]',
                'opacity-0 overflow-hidden transition-all duration-150 ease-out',
            )
        default:
            return ''
    }
}

export interface AnimatedPagesProps {
    /** 1-based page number. The first child renders when `pageNumber` is 1. */
    pageNumber: number
    /** Page elements rendered as direct children. Each child maps to a page number starting at 1. */
    children: ReactNode
    /** Overrides the default slide + fade animation. Returns Tailwind classes for a given transition status and direction. */
    getStatusClassName?: (status: TransitionStatus, isForward: boolean) => string
    /** Overrides for the `react-transition-state` state machine (e.g. custom timeout durations). */
    transitionMapOptions?: Partial<TransitionMapOptions<number>>
    /** Tailwind transition classes for the container height animation (e.g. duration, easing). */
    containerTransitionClassName?: string
    /** Resets the parent scroll frame to the top whenever the active page changes. */
    scrollToTop?: boolean
    /** Additional class name(s) for the outer grid container. */
    className?: string
}

/**
 * Renders children as pages with animated transitions between them.
 * During transitions, both the exiting and entering pages are rendered simultaneously.
 * Pages slide and fade based on the navigation direction (forward/backward).
 *
 * For custom animations, provide `getStatusClassName` with Tailwind classes
 * that cover both visual state (transform, opacity) and transition properties
 * (duration, easing). When using custom durations, pass matching `transitionMapOptions`
 * (e.g. `{ timeout: { enter: 300, exit: 150 } }`) so the state machine stays in sync.
 */
const AnimatedPages = ({
    pageNumber,
    children,
    getStatusClassName = defaultGetStatusClassName,
    transitionMapOptions,
    containerTransitionClassName = 'transition-[height] duration-300 ease-out',
    scrollToTop = false,
    className,
}: AnimatedPagesProps) => {
    const previousPageNumberRef = useRef(pageNumber)
    const isForwardRef = useRef(true)
    const containerRef = useRef<HTMLDivElement>(null)
    const activePageRef = useRef<HTMLDivElement>(null)
    const heightBeforeTransitionRef = useRef<number | null>(null)

    const { stateMap, setItem, toggle } = useTransitionMap<number>({
        ...DEFAULT_TRANSITION_MAP_OPTIONS,
        ...transitionMapOptions,
    })
    const isTransitioning = Array.from(stateMap.values()).some(
        ({ status }) => status !== 'entered' && status !== 'unmounted',
    )

    useLayoutEffect(() => {
        if (previousPageNumberRef.current === pageNumber) {
            // Initial render: start from the entered state without animation
            setItem(pageNumber, { initialEntered: true })
            return
        }

        if (scrollToTop) {
            scrollToPageTop(containerRef.current)
        }

        // Animate to next or previous page
        setItem(pageNumber)
        isForwardRef.current = pageNumber > previousPageNumberRef.current
        previousPageNumberRef.current = pageNumber
        heightBeforeTransitionRef.current =
            containerRef.current?.getBoundingClientRect().height ?? null
        toggle(pageNumber, true)
    }, [pageNumber, scrollToTop, setItem, toggle])

    useLayoutEffect(() => {
        if (!containerTransitionClassName) return

        const activeEl = activePageRef.current
        const containerEl = containerRef.current
        const startHeight = heightBeforeTransitionRef.current

        if (!activeEl || !containerEl || startHeight === null) return

        if (isTransitioning) {
            if (!containerEl.style.height) {
                // Set the height to the start height if it's not already set
                containerEl.style.height = `${startHeight}px`

                // Force reflow to ensure the start height is applied before transitioning
                void containerEl.getBoundingClientRect()
            }

            // Set the height to the target height during the transition
            const targetHeight = activeEl.getBoundingClientRect().height
            containerEl.style.height = `${targetHeight}px`
        } else {
            // Clean up the height property after the page transition is complete to allow for natural height changes
            containerEl.style.removeProperty('height')
            heightBeforeTransitionRef.current = null
        }
    }, [containerTransitionClassName, isTransitioning, stateMap])

    const childrenArray = Children.toArray(children)

    // Until the first layout effect registers the page, which never happens in a server render,
    // show it as already entered, so the server HTML and its hydration carry the initial page.
    const pageStatuses: [number, TransitionStatus][] =
        stateMap.size > 0
            ? Array.from(stateMap, ([pageIndex, { status }]) => [pageIndex, status])
            : [[pageNumber, 'entered']]

    return (
        <div
            ref={containerRef}
            className={twMerge(
                'grid w-full items-start',
                containerTransitionClassName,
                // While the height animates, the incoming page is already at full size; unclipped,
                // it overflows the container and flashes a scrollbar in a scrolling parent.
                isTransitioning && 'overflow-hidden',
                className,
            )}>
            {pageStatuses.map(([pageIndex, status]) => {
                if (status === 'unmounted') return null

                const childIndex = pageIndex - 1
                if (childIndex < 0 || childIndex >= childrenArray.length) return null

                return (
                    <div
                        key={pageIndex}
                        ref={pageIndex === pageNumber ? activePageRef : undefined}
                        className={clsx(
                            'col-start-1 row-start-1 w-full',
                            pageIndex === pageNumber ? 'z-10' : 'z-0',
                            getStatusClassName(status, isForwardRef.current),
                        )}>
                        {childrenArray[childIndex]}
                    </div>
                )
            })}
        </div>
    )
}

export default AnimatedPages
